import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import {
  Plus, MapPin, Maximize, DoorOpen, Search, X, Building2, Pencil, Trash2,
  Upload, Star, Users, Loader2, Check, Eye, Images, ChevronLeft, ChevronRight, FileText, Sparkles, Wand2, GripVertical, ArrowUpDown,
} from "lucide-react";
import OcrReviewModal from "../../components/OcrReviewModal";
import { Palmtree, Compass } from "lucide-react";
import { EmptyState, openOnboarding, openGuidedProperty } from "../../components/onboarding/EmptyState";
import { EMPTY as EMPTY_TXT } from "../../content/onboardingTexts";
import { completeness } from "../../components/onboarding/ActivationFunnel";
import { LaterSection } from "../../components/onboarding/LaterSection";
import api from "../../lib/api";
import { OwnersManager } from "../../components/dashboard/OwnersManager";
import { toast } from "sonner";
import {
  PROPERTY_TYPES, RENTAL_STATUSES, RENTAL_TYPES, ENERGY_CLASSES, COUNTRIES,
} from "../../lib/propertyOptions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const EMPTY_FORM = {
  owner_id: "", owner_name: "", name: "", type: "Appartement",
  address: "", address2: "", building: "", stair: "", floor: "", number: "",
  city: "", postal_code: "", region: "", country: "France",
  surface: "", rooms: "", bedrooms: "", bathrooms: "", construction_date: "", description: "",
  rental_status: "Disponible", rental_type: "Location nue",
  rent: "", charges: "", deposit: "", energy_class: "", dpe_expiry: "", notes: "",
  photos: [], image: "",
};

const STATUS_BADGE = {
  "Loué": "bg-brand-lime text-brand-greenDeep",
  "Disponible": "bg-emerald-500 text-white",
  "Recherche de locataire": "bg-blue-500 text-white",
  "Préavis / Départ": "bg-amber-500 text-white",
  "Travaux": "bg-orange-500 text-white",
  "Indisponible": "bg-gray-400 text-white",
};

function BienReorderRow({ id, name, badge }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={id} dragListener={false} dragControls={controls} className="flex items-center gap-3 bg-white border border-brand-soft rounded-2xl px-4 py-3">
      <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-brand-green/40 hover:text-brand-greenDeep touch-none p-1" data-testid={`bien-drag-${id}`}><GripVertical className="w-4 h-4" /></button>
      <span className="font-semibold text-brand-greenDeep text-sm flex-1 truncate">{name}</span>
      {badge && <span className="text-xs text-brand-green/50 shrink-0">{badge}</span>}
    </Reorder.Item>
  );
}

export default function Biens() {
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const docRef = useRef(null);

  const PROP_OCR_FIELDS = [
    { key: "type", label: "Type de bien" },
    { key: "owner_name", label: "Propriétaire" },
    { key: "address", label: "Adresse" },
    { key: "address2", label: "Complément" },
    { key: "city", label: "Ville" },
    { key: "postal_code", label: "Code postal" },
    { key: "region", label: "Région" },
    { key: "surface", label: "Surface (m²)" },
    { key: "rooms", label: "Pièces" },
    { key: "bedrooms", label: "Chambres" },
    { key: "bathrooms", label: "Salles de bain" },
    { key: "construction_date", label: "Année construction" },
    { key: "energy_class", label: "Classe DPE" },
    { key: "description", label: "Description" },
    { key: "cadastral_ref", label: "Réf. cadastrale (→ notes)" },
  ];
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [showNewOwner, setShowNewOwner] = useState(false);
  const [newOwner, setNewOwner] = useState({ name: "", email: "" });
  const fileRef = useRef(null);
  const [gallery, setGallery] = useState(null); // {photos, index}
  const [addrSug, setAddrSug] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [cardOrder, setCardOrder] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderIds, setReorderIds] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const addrTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [loaded, setLoaded] = useState(false);
  const loadProps = () => api.get("/properties").then((r) => { setItems(r.data); setLoaded(true); }).catch(() => {});
  useEffect(() => { const h = () => loadProps(); window.addEventListener("onboarding-closed", h); return () => window.removeEventListener("onboarding-closed", h); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadOwners = () => api.get("/owners").then((r) => setOwners(r.data)).catch(() => {});
  useEffect(() => {
    loadProps(); loadOwners();
    api.get("/ui/card-order/properties").then((r) => setCardOrder(r.data.order || [])).catch(() => {});
  }, []);

  // open edit modal when navigated from detail page with editId
  useEffect(() => {
    const editId = location.state?.editId;
    if (editId && items.length) {
      const p = items.find((it) => it.id === editId);
      if (p) { openEdit(p); navigate(location.pathname, { replace: true, state: {} }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, items]);

  const orderIdx = (id) => { const i = cardOrder.indexOf(id); return i === -1 ? 1e9 : i; };
  const orderedItems = [...items].sort((a, b) => orderIdx(a.id) - orderIdx(b.id));
  const filtered = orderedItems.filter((p) =>
    (filter === "Tous" || p.status === filter) &&
    ((p.name || "").toLowerCase().includes(query.toLowerCase()) ||
      (p.address || "").toLowerCase().includes(query.toLowerCase()))
  );

  const startReorder = () => { setReorderIds(orderedItems.map((p) => p.id)); setReorderMode(true); };
  const saveReorder = async () => {
    setSavingOrder(true);
    try {
      await api.put("/ui/card-order/properties", { order: reorderIds });
      setCardOrder(reorderIds); setReorderMode(false); toast.success("Ordre des biens enregistré");
    } catch { toast.error("Erreur"); } finally { setSavingOrder(false); }
  };

  const [errors, setErrors] = useState({});
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: false } : e)); };

  const openCreate = () => {
    setErrors({});
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowNewOwner(false);
    setDocs([]);
    setOpen(true);
  };

  const openEdit = (p) => {
    setErrors({});
    setForm({
      ...EMPTY_FORM, ...p,
      surface: p.surface ?? "", rooms: p.rooms ?? "", bedrooms: p.bedrooms ?? "",
      bathrooms: p.bathrooms ?? "", rent: p.rent ?? "", charges: p.charges ?? "", deposit: p.deposit ?? "",
      photos: p.photos || (p.image ? [p.image] : []),
      image: p.image || "",
    });
    setEditingId(p.id);
    setShowNewOwner(false);
    setDocs([]);
    setOpen(true);
  };

  const addDocs = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setDocs((d) => [...d, ...files.map((f) => ({ file: f, name: f.name }))]);
    if (docRef.current) docRef.current.value = "";
  };
  const removeDoc = (i) => setDocs((d) => d.filter((_, idx) => idx !== i));

  const runOcr = async () => {
    if (!docs.length) { toast.error("Ajoutez au moins un document à analyser"); return; }
    setOcrBusy(true);
    try {
      const fd = new FormData();
      docs.forEach((d) => fd.append("files", d.file));
      const { data } = await api.post("/ai/extract-property-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOcrResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse OCR impossible");
    } finally { setOcrBusy(false); }
  };

  const applyOcr = (selected) => {
    setForm((f) => {
      const next = { ...f };
      Object.entries(selected).forEach(([k, v]) => {
        if (k === "cadastral_ref") {
          const line = `Réf. cadastrale : ${v}`;
          if (!(next.notes || "").includes(v)) next.notes = next.notes ? `${next.notes}\n${line}` : line;
        } else if (k === "type") {
          if (PROPERTY_TYPES.includes(v)) next.type = v;
        } else {
          next[k] = v;
        }
      });
      return next;
    });
    const n = Object.keys(selected).length;
    toast.success(n ? `${n} champ(s) appliqué(s) au formulaire` : "Aucun champ sélectionné");
  };

  const attachDocs = async (pid) => {
    for (const d of docs) {
      try {
        const fd = new FormData();
        fd.append("file", d.file);
        fd.append("category", "Titre de propriété");
        await api.post(`/properties/${pid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch { /* ignore un échec ponctuel d'attachement */ }
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/properties/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => {
          const photos = [...f.photos, res.data.url];
          return { ...f, photos, image: f.image || res.data.url };
        });
      }
      toast.success("Photo(s) ajoutée(s)");
    } catch (err) {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (url) => {
    setForm((f) => {
      const photos = f.photos.filter((p) => p !== url);
      const image = f.image === url ? (photos[0] || "") : f.image;
      return { ...f, photos, image };
    });
  };

  const onAddressChange = (val) => {
    set("address", val);
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (val.trim().length < 3) { setAddrSug([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/geo/address-search?q=${encodeURIComponent(val)}`);
        setAddrSug(res.data || []);
        setAddrOpen((res.data || []).length > 0);
      } catch { setAddrSug([]); }
    }, 350);
  };

  const pickAddress = (s) => {
    setForm((f) => ({
      ...f, address: s.street || s.label, city: s.city || f.city,
      postal_code: s.postcode || f.postal_code, region: s.region || f.region, country: "France",
    }));
    setAddrOpen(false);
    setAddrSug([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    const req = {
      name: !form.name.trim(),
      address: !form.address.trim(),
      city: !form.city.trim(),
      postal_code: !form.postal_code.trim(),
      country: !form.country,
    };
    if (Object.values(req).some(Boolean)) {
      setErrors(req);
      toast.error("Veuillez renseigner les champs obligatoires (en rouge)");
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const owner = owners.find((o) => o.id === form.owner_id);
      const payload = {
        ...form,
        owner_name: owner ? owner.name : form.owner_name || "",
        surface: Number(form.surface) || 0,
        rooms: Number(form.rooms) || 0,
        bedrooms: Number(form.bedrooms) || 0,
        bathrooms: Number(form.bathrooms) || 0,
        rent: Number(form.rent) || 0,
        charges: Number(form.charges) || 0,
        deposit: Number(form.deposit) || 0,
        image: form.image || form.photos[0] || "",
      };
      if (editingId) {
        const res = await api.put(`/properties/${editingId}`, payload);
        if (docs.length) await attachDocs(editingId);
        setItems((its) => its.map((it) => (it.id === editingId ? res.data : it)));
        toast.success(docs.length ? "Bien modifié · documents attachés" : "Bien modifié");
      } else {
        const res = await api.post("/properties", payload);
        if (docs.length && res.data?.id) await attachDocs(res.data.id);
        setItems((its) => [res.data, ...its]);
        toast.success(docs.length ? "Bien ajouté · documents attachés" : "Bien ajouté");
      }
      setDocs([]);
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail) {
        toast.error(detail, { action: { label: "Voir les offres", onClick: () => navigate("/app/profil") } });
      } else {
        toast.error(detail || "Erreur lors de l'enregistrement");
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteProperty = async (p) => {
    if (!window.confirm(`Supprimer le bien « ${p.name} » ?`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      setItems((its) => its.filter((it) => it.id !== p.id));
      toast.success("Bien supprimé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };

  // ---- owners ----
  const createOwnerInline = async () => {
    if (!newOwner.name.trim()) { toast.error("Nom du propriétaire requis"); return; }
    try {
      const res = await api.post("/owners", newOwner);
      setOwners((o) => [...o, res.data]);
      set("owner_id", res.data.id);
      setNewOwner({ name: "", email: "" });
      setShowNewOwner(false);
      toast.success("Propriétaire ajouté");
    } catch { toast.error("Ajout impossible"); }
  };

  if (loaded && items.length === 0) return <EmptyState icon={Building2} title={EMPTY_TXT.biens.title} cta={EMPTY_TXT.biens.cta} demo onCta={() => openOnboarding(1)} testid="empty-biens" />;
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-wrap gap-2">
          {["Tous", "Occupé", "Vacant"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === f ? "bg-brand-lime text-brand-greenDeep" : "bg-white text-brand-green/60 border border-brand-soft hover:bg-brand-softer"}`}>
              {f}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-soft w-full sm:w-56">
            <Search className="w-4 h-4 text-brand-green/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un bien" className="bg-transparent outline-none text-sm w-full" data-testid="search-input" />
          </div>
          <button onClick={() => setOwnersOpen(true)} data-testid="manage-owners-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-brand-soft text-brand-green font-semibold text-sm whitespace-nowrap hover:bg-brand-softer transition-colors">
            <Users className="w-4 h-4" /> Propriétaires
          </button>
          {reorderMode ? (
            <>
              <button onClick={() => setReorderMode(false)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-brand-soft text-brand-green/70 font-semibold text-sm whitespace-nowrap hover:bg-brand-softer transition-colors" data-testid="cancel-reorder-btn">Annuler</button>
              <button onClick={saveReorder} disabled={savingOrder} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm whitespace-nowrap transition-colors disabled:opacity-60" data-testid="save-reorder-btn">{savingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Terminer</button>
            </>
          ) : (
            <button onClick={startReorder} disabled={items.length < 2} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-brand-soft text-brand-green font-semibold text-sm whitespace-nowrap hover:bg-brand-softer transition-colors disabled:opacity-50" data-testid="reorder-btn">
              <ArrowUpDown className="w-4 h-4" /> Réorganiser
            </button>
          )}
          <button onClick={openGuidedProperty} data-testid="add-property-guided-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-brand-soft text-brand-greenDeep font-semibold text-sm whitespace-nowrap hover:bg-brand-softer transition-colors">
            <Compass className="w-4 h-4 text-brand-limeDark" /> Ajouter un bien guidé
          </button>
          <button onClick={openCreate} data-testid="add-property-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm whitespace-nowrap transition-colors">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      {reorderMode ? (
        <div className="space-y-3" data-testid="reorder-list">
          <p className="text-sm text-brand-green/55 bg-brand-lime/10 border border-brand-lime/30 rounded-xl px-4 py-3">Glissez les biens par la poignée pour définir leur ordre d'affichage, puis cliquez sur « Terminer ».</p>
          <Reorder.Group axis="y" values={reorderIds} onReorder={setReorderIds} className="space-y-2">
            {reorderIds.map((id) => {
              const p = items.find((it) => it.id === id);
              if (!p) return null;
              return <BienReorderRow key={id} id={id} name={p.name} badge={[p.city, p.tenant || "Aucun locataire"].filter(Boolean).join(" · ")} />;
            })}
          </Reorder.Group>
        </div>
      ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl overflow-hidden border border-brand-soft hover:shadow-xl transition-all group" data-testid={`property-card-${p.id}`}>
            <div className="h-44 overflow-hidden relative">
              <img src={imgSrc(p.image)} alt={p.name} onClick={() => setGallery({ photos: (p.photos && p.photos.length ? p.photos : [p.image]).filter(Boolean), index: 0 })} className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-500" data-testid={`property-image-${p.id}`} />
              <span className={`absolute top-3 right-3 text-xs px-3 py-1 rounded-full font-semibold ${STATUS_BADGE[p.rental_status] || (p.status === "Occupé" ? "bg-brand-lime text-brand-greenDeep" : "bg-orange-500 text-white")}`}>{p.rental_status || p.status}</span>
              <span className="absolute top-3 left-3 text-xs px-3 py-1 rounded-full font-medium bg-white/90 text-brand-green">{p.type}</span>
              {p.mode_location && p.mode_location !== "longue_duree" && <span className="absolute top-11 left-3 text-[11px] px-2.5 py-0.5 rounded-full font-semibold bg-brand-lime text-brand-greenDeep inline-flex items-center gap-1" data-testid={`str-badge-${p.id}`}><Palmtree className="w-3 h-3" /> {p.mode_location === "mixte" ? "Mixte" : "Saisonnier"}</span>}
              {p.photos && p.photos.length > 1 && (
                <span className="absolute bottom-3 left-3 text-xs px-2.5 py-1 rounded-full bg-black/60 text-white flex items-center gap-1"><Images className="w-3.5 h-3.5" /> {p.photos.length}</span>
              )}
              <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => navigate(`/app/biens/${p.id}`)} data-testid={`view-property-${p.id}`} className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center text-brand-greenDeep hover:bg-white shadow"><Eye className="w-4 h-4" /></button>
                <button onClick={() => navigate(`/app/saisonnier/biens/${p.id}`)} title={p.mode_location && p.mode_location !== "longue_duree" ? "Paramètres saisonniers" : "Passer en location saisonnière"} data-testid={`str-property-${p.id}`} className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center text-brand-greenDeep hover:bg-white shadow"><Palmtree className="w-4 h-4" /></button>
                <button onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`} className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center text-brand-greenDeep hover:bg-white shadow"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => deleteProperty(p)} data-testid={`delete-property-${p.id}`} className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center text-red-500 hover:bg-white shadow"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 onClick={() => navigate(`/app/biens/${p.id}`)} className="font-semibold text-lg text-brand-greenDeep cursor-pointer hover:text-brand-limeDark transition-colors" data-testid={`property-title-${p.id}`}>{p.name}</h3>
                {p.owner_name && <span className="text-xs text-brand-green/50 truncate max-w-[120px]">{p.owner_name}</span>}
              </div>
              <p className="flex items-center gap-1.5 text-sm text-brand-green/50 mt-1"><MapPin className="w-3.5 h-3.5" /> {[p.address, p.city].filter(Boolean).join(", ")}</p>
              <div className="flex items-center gap-4 mt-4 text-sm text-brand-green/60">
                <span className="flex items-center gap-1.5"><Maximize className="w-4 h-4" /> {p.surface} m²</span>
                {p.rooms > 0 && <span className="flex items-center gap-1.5"><DoorOpen className="w-4 h-4" /> {p.rooms} p.</span>}
                {completeness(p) < 100 && <button onClick={() => openEdit(p)} title="Complétez la fiche : surface, DPE, photos… (non bloquant)" className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100" data-testid={`property-completeness-${p.id}`}>Fiche complète à {completeness(p)} %</button>}
                {p.energy_class && <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${["F", "G"].includes((p.energy_class || "").toUpperCase()) ? "bg-red-100 text-red-600" : "bg-brand-softer"}`} title={["F", "G"].includes((p.energy_class || "").toUpperCase()) ? "Passoire thermique" : ""}>DPE {p.energy_class}{["F", "G"].includes((p.energy_class || "").toUpperCase()) ? " ⚠" : ""}</span>}
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-brand-soft">
                <div>
                  <p className="text-xs text-brand-green/50">Loyer CC</p>
                  <p className="font-bold text-brand-greenDeep">{((p.rent || 0) + (p.charges || 0)).toLocaleString("fr-FR")} €</p>
                </div>
                <p className="text-sm text-brand-green/60">{p.tenant || "Aucun locataire"}</p>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-brand-green/50">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Aucun bien trouvé.
          </div>
        )}
      </div>
      )}

      {/* Create / Edit modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="property-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">{editingId ? "Modifier le bien" : "Nouveau bien"}</h3>
              <button onClick={() => setOpen(false)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={submit} className="space-y-8">
              {/* Affectation */}
              <Section title="Affectation">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Propriétaire">
                    <div className="flex gap-2">
                      <select value={form.owner_id} onChange={(e) => set("owner_id", e.target.value)} className="input" data-testid="owner-select">
                        <option value="">— Sélectionner —</option>
                        {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                      <button type="button" onClick={() => setShowNewOwner((s) => !s)} className="px-3 rounded-xl bg-brand-softer text-brand-greenDeep hover:bg-brand-soft" data-testid="toggle-new-owner"><Plus className="w-4 h-4" /></button>
                    </div>
                    {showNewOwner && (
                      <div className="mt-2 p-3 rounded-xl bg-brand-softer space-y-2">
                        <input value={newOwner.name} onChange={(e) => setNewOwner({ ...newOwner, name: e.target.value })} placeholder="Nom du propriétaire" className="input" data-testid="new-owner-name" />
                        <input value={newOwner.email} onChange={(e) => setNewOwner({ ...newOwner, email: e.target.value })} placeholder="Email (optionnel)" className="input" data-testid="new-owner-email" />
                        <button type="button" onClick={createOwnerInline} className="w-full py-2 rounded-xl bg-brand-greenDeep text-brand-lime text-sm font-medium" data-testid="save-new-owner">Ajouter le propriétaire</button>
                      </div>
                    )}
                  </Field>
                  <Field label="Type de bien">
                    <select value={form.type} onChange={(e) => set("type", e.target.value)} className="input" data-testid="type-select">
                      {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Identifiant *" error={errors.name} hint="Référence ou numéro unique. Vous pouvez inventer une référence libre.">
                  <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex : APT-RIVOLI-01" className="input" data-testid="identifier-input" />
                </Field>
              </Section>

              {/* Adresse */}
              <Section title="Adresse">
                <Field label="Adresse *" error={errors.address} hint="Commencez à saisir : les suggestions remplissent ville et code postal (France).">
                  <div className="relative">
                    <input value={form.address} onChange={(e) => onAddressChange(e.target.value)} onFocus={() => addrSug.length && setAddrOpen(true)} onBlur={() => setTimeout(() => setAddrOpen(false), 150)} placeholder="12 Rue de Rivoli" className="input" data-testid="address-input" autoComplete="off" />
                    {addrOpen && addrSug.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white rounded-xl border border-brand-soft shadow-lg overflow-hidden" data-testid="address-suggestions">
                        {addrSug.map((s, i) => (
                          <button type="button" key={i} onMouseDown={(e) => { e.preventDefault(); pickAddress(s); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-brand-softer border-b border-brand-soft last:border-0 flex items-center gap-2" data-testid={`address-suggestion-${i}`}>
                            <MapPin className="w-3.5 h-3.5 text-brand-green/40 shrink-0" /> <span className="truncate">{s.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Field>
                <Field label="Adresse 2"><input value={form.address2} onChange={(e) => set("address2", e.target.value)} className="input" /></Field>
                <div className="grid sm:grid-cols-4 gap-4">
                  <Field label="Bâtiment"><input value={form.building} onChange={(e) => set("building", e.target.value)} className="input" /></Field>
                  <Field label="Escalier"><input value={form.stair} onChange={(e) => set("stair", e.target.value)} className="input" /></Field>
                  <Field label="Étage"><input value={form.floor} onChange={(e) => set("floor", e.target.value)} className="input" /></Field>
                  <Field label="Numéro"><input value={form.number} onChange={(e) => set("number", e.target.value)} className="input" /></Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Ville *" error={errors.city}><input value={form.city} onChange={(e) => set("city", e.target.value)} className="input" data-testid="city-input" /></Field>
                  <Field label="Code postal *" error={errors.postal_code}><input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="input" data-testid="postal-input" /></Field>
                  <Field label="Région"><input value={form.region} onChange={(e) => set("region", e.target.value)} className="input" /></Field>
                </div>
                <Field label="Pays *" error={errors.country}>
                  <select value={form.country} onChange={(e) => set("country", e.target.value)} className="input" data-testid="country-select">
                    {COUNTRIES.map((c, i) => <option key={`${c}-${i}`} value={c}>{c}</option>)}
                  </select>
                </Field>
              </Section>

              {/* Description */}
              <Section later title="Description">
                <div className="grid sm:grid-cols-4 gap-4">
                  <Field label="Superficie (m²)"><input type="number" value={form.surface} onChange={(e) => set("surface", e.target.value)} className="input" /></Field>
                  <Field label="Nb de pièces"><input type="number" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} className="input" /></Field>
                  <Field label="Nb de chambres"><input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="input" /></Field>
                  <Field label="Salles de bain"><input type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className="input" /></Field>
                </div>
                <Field label="Année de construction"><input type="number" inputMode="numeric" min="1700" max={new Date().getFullYear() + 5} step="1" value={form.construction_date} onChange={(e) => set("construction_date", e.target.value)} placeholder="Ex : 1995" className="input" data-testid="construction-year-input" /></Field>
                <Field label="Description" hint="Type, taille, aménagements, confort et particularités (repris sur le contrat de location).">
                  <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="input resize-none" />
                </Field>
              </Section>

              {/* Informations locatives */}
              <Section title="Informations locatives">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="État locatif">
                    <select value={form.rental_status} onChange={(e) => set("rental_status", e.target.value)} className="input" data-testid="rental-status-select">
                      {RENTAL_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Type de location proposé">
                    <select value={form.rental_type} onChange={(e) => set("rental_type", e.target.value)} className="input" data-testid="rental-type-select">
                      {RENTAL_TYPES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Loyer hors charges (€)"><input type="number" value={form.rent} onChange={(e) => set("rent", e.target.value)} className="input" data-testid="rent-input" /></Field>
                  <Field label="Charges locatives (€)"><input type="number" value={form.charges} onChange={(e) => set("charges", e.target.value)} className="input" /></Field>
                  <Field label="Dépôt de garantie (€)"><input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} className="input" /></Field>
                </div>
                <Field label="Classe énergie (DPE)">
                  <div className="flex gap-2 flex-wrap">
                    {ENERGY_CLASSES.map((c) => (
                      <button type="button" key={c} onClick={() => set("energy_class", form.energy_class === c ? "" : c)}
                        className={`w-10 h-10 rounded-xl font-bold transition-colors ${form.energy_class === c ? "bg-brand-greenDeep text-brand-lime" : "bg-brand-softer text-brand-green/60 hover:bg-brand-soft"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Date d'expiration du DPE"><input type="date" value={form.dpe_expiry || ""} onChange={(e) => set("dpe_expiry", e.target.value)} className="input max-w-xs" data-testid="dpe-expiry-input" /></Field>
              </Section>

              {/* Documents & OCR */}
              <Section title="Documents du bien & pré-remplissage (OCR)">
                <p className="text-xs text-brand-green/50 -mt-2 mb-2">Ajoutez le titre de propriété, l'acte notarié, le DPE ou la taxe foncière (PDF ou image). L'IA peut lire ces documents et pré-remplir le formulaire. Les fichiers sont attachés au bien à l'enregistrement.</p>
                <div className="space-y-2">
                  {docs.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 bg-brand-softer rounded-xl px-3 py-2" data-testid={`prop-doc-${i}`}>
                      <FileText className="w-4 h-4 text-brand-greenDeep shrink-0" />
                      <span className="text-sm text-brand-greenDeep truncate flex-1">{d.name}</span>
                      <button type="button" onClick={() => removeDoc(i)} className="text-brand-green/40 hover:text-red-500" data-testid={`prop-doc-remove-${i}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <button type="button" onClick={() => docRef.current?.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="add-prop-doc-btn"><Upload className="w-4 h-4" /> Ajouter un document</button>
                  <button type="button" onClick={runOcr} disabled={ocrBusy || !docs.length} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-50" data-testid="ocr-prefill-prop-btn">{ocrBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Analyser (OCR) et pré-remplir</button>
                  <input ref={docRef} type="file" accept=".pdf,image/*" multiple className="hidden" onChange={addDocs} data-testid="prop-doc-input" />
                </div>
              </Section>

              {/* Photos */}
              <Section later title="Photos">
                <p className="text-xs text-brand-green/50 -mt-2 mb-1">Cliquez sur l'étoile pour définir la photo par défaut (vignette).</p>
                <div className="flex flex-wrap gap-3">
                  {form.photos.map((url) => (
                    <div key={url} className="relative w-28 h-28 rounded-xl overflow-hidden border border-brand-soft group" data-testid="photo-thumb">
                      <img src={imgSrc(url)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => set("image", url)} title="Définir par défaut"
                        className={`absolute top-1 left-1 w-7 h-7 rounded-full flex items-center justify-center ${form.image === url ? "bg-brand-lime text-brand-greenDeep" : "bg-black/50 text-white hover:bg-black/70"}`} data-testid="set-default-photo">
                        <Star className="w-3.5 h-3.5" fill={form.image === url ? "currentColor" : "none"} />
                      </button>
                      <button type="button" onClick={() => removePhoto(url)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/50 text-white hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-photo">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      {form.image === url && <span className="absolute bottom-0 inset-x-0 text-[10px] text-center bg-brand-lime text-brand-greenDeep font-semibold py-0.5">Par défaut</span>}
                    </div>
                  ))}
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="w-28 h-28 rounded-xl border-2 border-dashed border-brand-soft flex flex-col items-center justify-center text-brand-green/50 hover:border-brand-limeDark hover:text-brand-greenDeep transition-colors" data-testid="upload-photo-btn">
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6 mb-1" /><span className="text-xs">Ajouter</span></>}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="photo-file-input" />
                </div>
              </Section>

              {/* Notes */}
              <Section later title="Notes / Commentaires">
                <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Notes internes..." className="input resize-none" data-testid="notes-input" />
              </Section>

              <div className="flex gap-3 pt-2 sticky bottom-0 bg-white">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-green font-semibold">Annuler</button>
                <button type="submit" disabled={saving} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="submit-property-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingId ? "Enregistrer les modifications" : "Créer le bien"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Owners manager */}
      {ownersOpen && (
        <OwnersManager
          owners={owners}
          onClose={() => setOwnersOpen(false)}
          reload={loadOwners}
          reloadProps={loadProps}
        />
      )}

      {/* Gallery lightbox */}
      {gallery && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" onClick={() => setGallery(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setGallery(null)} data-testid="gallery-close"><X className="w-7 h-7" /></button>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={imgSrc(gallery.photos[gallery.index])} alt="" className="w-full max-h-[80vh] object-contain rounded-2xl" />
            {gallery.photos.length > 1 && (
              <>
                <button onClick={() => setGallery((g) => ({ ...g, index: (g.index - 1 + g.photos.length) % g.photos.length }))} data-testid="lightbox-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-brand-greenDeep"><ChevronLeft className="w-6 h-6" /></button>
                <button onClick={() => setGallery((g) => ({ ...g, index: (g.index + 1) % g.photos.length }))} data-testid="lightbox-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-brand-greenDeep"><ChevronRight className="w-6 h-6" /></button>
                <div className="flex justify-center gap-2 mt-4">
                  {gallery.photos.map((url, i) => (
                    <button key={url + i} onClick={() => setGallery((g) => ({ ...g, index: i }))} className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${i === gallery.index ? "border-brand-lime" : "border-white/30"}`}>
                      <img src={imgSrc(url)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <OcrReviewModal
        open={!!ocrResult}
        onClose={() => setOcrResult(null)}
        detected={ocrResult?.fields || {}}
        current={form}
        fields={PROP_OCR_FIELDS}
        confidence={ocrResult?.confidence}
        summary={ocrResult?.summary}
        onApply={applyOcr}
      />

      <style>{`.input{width:100%;padding:0.7rem 1rem;border-radius:1rem;border:1px solid #efe7d6;outline:none;transition:all .2s;background:#fff}.input:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.25)}.field-error .input{border-color:#ef4444!important;box-shadow:0 0 0 3px rgba(239,68,68,.15)}`}</style>
    </div>
  );
}

function Section({ title, children, later }) {
  if (later) return <LaterSection title={title}>{children}</LaterSection>;
  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-brand-greenDeep border-b border-brand-soft pb-2">{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, hint, children, error }) {
  return (
    <div className={error ? "field-error" : ""}>
      <label className={`block text-sm font-medium mb-1.5 ${error ? "text-red-600" : "text-brand-green/70"}`}>{label}</label>
      {children}
      {error ? <p className="text-xs text-red-600 mt-1">Ce champ obligatoire est requis</p> : (hint && <p className="text-xs text-brand-green/45 mt-1">{hint}</p>)}
    </div>
  );
}

